import React, { FC, useEffect, useState } from "react";
import { Formik } from "formik";
import { TimerContainer } from "./styled";
import MagicCodeInput from "../../MagicCode/MagicCodeInput";
import {
  FilledButton as Button,
  Stack,
  Text,
  theme,
} from "@ifgengineering/component-library";
import axios from "axios";
import { toast } from "react-toastify";
import { BackContainer } from "./styled";
import Icon from "@icon-park/react/es/all";
import { format } from "date-fns";
import { otpCodeSchema } from "./validationSchemas";
import { COUNTDOWN_TIME_IN_SECONDS } from "../../../../../consts";
import useInterval from "../../MagicCode/useInterval";
import { useClientAuthComponents } from "../../../../Context";
import { Container } from "../../styled";

interface OTPCodeFormProps {
  title: string;
  email: string;
  handleBackButton: VoidFunction;
  handleSuccess: VoidFunction;
  handleFailure: VoidFunction;
}

const COUNT_TIME = COUNTDOWN_TIME_IN_SECONDS * 1000;
const DELAY = 1000;

export const AppleLinkOTPForm: FC<OTPCodeFormProps> = ({
  title,
  email,
  handleBackButton,
  handleSuccess,
  handleFailure,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showResentOtpMessage, setShowResentOtpMessage] = useState(false);
  const [allowSendCode, setAllowSendCode] = useState(false);
  const [formattedTime, setFormattedTime] = useState("01:00");
  const [count, setCount] = useState<number>(COUNT_TIME);
  const [isPlaying, setPlaying] = useState<boolean>(false);

  const { appleAuthData, appleLinkEmail, generateOTP, logException } =
    useClientAuthComponents();

  useInterval(
    () => {
      setCount(count - 1000);
    },
    isPlaying ? DELAY : null
  );

  useEffect(() => {
    setPlaying(true);
  }, []);

  useEffect(() => {
    setFormattedTime(format(new Date(count), "mm:ss"));
    if (count <= 0) {
      setPlaying(false);
      setAllowSendCode(true);
    }
  }, [count]);

  const back = () => {
    handleBackButton();
  };

  const linkEmailToAppleId = async (otp: string) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      if (appleAuthData?.authorizationCode) {
        const { authorizationCode } = appleAuthData;
        await appleLinkEmail({
          existingAccountEmail: email,
          otp,
          authorizationCode,
        });
      }
      handleSuccess();
    } catch (e: any) {
      setIsLoading(false);
      if (axios.isAxiosError(e) && e.response && e.response.data) {
        const errorMessage = e.response.data["message"] as string;
        const isInvalidOtp = errorMessage.toLowerCase().includes("invalid otp");
        if (e.response?.status === 401 && isInvalidOtp) {
          setErrorMessage("Wrong code. Please try again");
          return;
        }
      }

      toast.error("Something went wrong. Try again or contact support");

      logException({
        email,
        tag: "appleLinkFlow_OTP_Form",
        exception: e,
      });

      handleFailure();
      throw e;
    }
  };

  const fadeResentOtpMessage = () => {
    setShowResentOtpMessage(true);

    setTimeout(() => {
      setCount(COUNT_TIME);
      setPlaying(true);
      setAllowSendCode(false);
      setShowResentOtpMessage(false);
    }, 3000);
  };

  const resendOTPCode = async () => {
    try {
      if (allowSendCode) {
        await generateOTP(email);
        fadeResentOtpMessage();
      }
    } catch (e: any) {
      if (e.response.status !== 404) {
        logException({
          email,
          tag: "appleLinkFlow_OTP_Form",
          exception: e,
        });
      }
    }
  };

  return (
    <Container gap={16} data-testid="otp">
      <BackContainer onClick={back}>
        <Icon
          type="ArrowLeft"
          size={18}
          theme="outline"
          fill={theme.colors.BLUE600}
        />
        <Text type="S16" color="BLUE600" fontFamily="Inter">
          Back
        </Text>
      </BackContainer>

      <Text type="T32" color="SLATE800" fontFamily="archiasemibold">
        {title}
      </Text>

      <Formik
        initialValues={{
          otp: "",
        }}
        validationSchema={otpCodeSchema}
        onSubmit={({ otp }) => {
          linkEmailToAppleId(otp);
        }}
      >
        {({
          errors,
          touched,
          submitForm,
          isValid,
          isSubmitting,
          values,
          setSubmitting,
        }) => {
          useEffect(() => {
            if (
              !isSubmitting &&
              isValid &&
              !!values.otp &&
              values.otp.length === 6
            ) {
              setSubmitting(true);
              submitForm();
            }
          }, [isSubmitting, isValid, values.otp]);

          const formikErrorMessage = touched.otp ? errors.otp : undefined;
          const message = errorMessage || formikErrorMessage;

          return (
            <Container gap={16}>
              <Stack orientation="vertical">
                <Text type="S16" color="SLATE800" fontFamily="Inter">
                  Enter OTP:
                </Text>
                <MagicCodeInput
                  name="otp"
                  numInputs={6}
                  isDisabled={isLoading}
                  hasErrored={Boolean(errors.otp)}
                  errorMessage={message}
                />
              </Stack>

              {email && email.trim() && (
                <Text type="P16" color="SLATE800">
                  We've sent a code to the following email:{" "}
                  <Text
                    type="H16"
                    color="SLATE800"
                    as="a"
                    href={`mailto:${email}`}
                  >
                    {email}
                  </Text>
                </Text>
              )}

              <Button
                testId="apple-link-button"
                type="submit"
                height="56px"
                text="Link my Apple ID"
                color="darkBlue"
                isLoading={isLoading}
                disabled={!isValid || isLoading}
                onClick={submitForm}
              />

              <div>
                {showResentOtpMessage ? (
                  <Text type="S16" color="SLATE800" fontFamily="Inter">
                    Code sent!
                  </Text>
                ) : (
                  <>
                    <TimerContainer>
                      <Text type="S16" color="SLATE600" fontFamily="Inter">
                        {formattedTime}
                      </Text>
                    </TimerContainer>
                    <Text
                      type="S16"
                      color={allowSendCode ? "BLUE600" : "SLATE450"}
                      fontFamily="Inter"
                      style={{ cursor: allowSendCode ? "pointer" : "" }}
                      onClick={resendOTPCode}
                    >
                      Resend Code
                    </Text>
                  </>
                )}
              </div>
            </Container>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AppleLinkOTPForm;
